import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Ic from "../../components/icons.jsx";

/**
 * Reusable 4-step header for the order-import wizard:
 * 1 Import (upload) → 2 AI analysis → 3 Review → 4 Order.
 *
 * Completed steps are clickable so the operator can always walk back and see
 * what happened. Default navigation: step 1 goes to /orders (start a new
 * import), step 4 opens the finalized order. Steps 2–3 are delegated to the
 * page via onStepClick because the review page renders both states itself.
 */
export type ImportStep = 1 | 2 | 3 | 4;

const STEP_KEYS = [
  "orders.import.step.upload",
  "orders.import.step.analysis",
  "orders.import.step.review",
  "orders.import.step.order",
] as const;

export function ImportStepper({
  current,
  completed,
  orderId,
  onStepClick,
}: {
  /** The step the user is looking at right now (highlighted). */
  current: ImportStep;
  /** Highest step already completed; steps <= completed are clickable. */
  completed: number;
  /** Finalized order id — enables clicking step 4. */
  orderId?: string | null;
  /** Page-level handler for steps 2 and 3 (the review page swaps its own view). */
  onStepClick?: (step: ImportStep) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  function clickable(step: ImportStep): boolean {
    if (step === current) return false;
    if (step === 4) return Boolean(orderId) && completed >= 4;
    return step <= completed;
  }

  function onClick(step: ImportStep) {
    if (!clickable(step)) return;
    if (step === 1) {
      navigate("/orders");
    } else if (step === 4 && orderId) {
      navigate(`/orders/${orderId}`);
    } else {
      onStepClick?.(step);
    }
  }

  return (
    <nav className="oi-stepper" aria-label={t("orders.import.title")}>
      {STEP_KEYS.map((key, i) => {
        const step = (i + 1) as ImportStep;
        const isDone = step < current || step <= completed;
        const isCurrent = step === current;
        const canClick = clickable(step);
        return (
          <span key={key} style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
            {i > 0 && <span className={`oi-conn${step <= completed || step <= current ? " done" : ""}`} />}
            <button
              type="button"
              className={[
                "oi-step",
                isCurrent ? "current" : "",
                !isCurrent && isDone ? "done" : "",
                canClick ? "clickable" : "",
              ].filter(Boolean).join(" ")}
              onClick={() => onClick(step)}
              disabled={!canClick && !isCurrent}
              aria-current={isCurrent ? "step" : undefined}
            >
              <span className="oi-dot">
                {!isCurrent && isDone ? <Ic.check /> : step}
              </span>
              {t(key)}
            </button>
          </span>
        );
      })}
    </nav>
  );
}
